import * as React from 'react';
import { t } from '@moovly/i18n';
import { Button, RadioButton } from '@moovly/ui';
import { ContractData, LicenseData } from '../../types/licenses';
import { formatDate } from '../../../../utils/date';
import './AssignSubscription.scss';
interface AssignSubscriptionProps {
  userId: string;
  contracts: ContractData[];
  license: LicenseData | undefined;
  canPurchaseAndAssign?: boolean;
  onGrantLicense: (contractId: string, licenseId: string) => void;
  onOrderAndAssignNewLicense: (contractId: string) => void;
}
type ContractOption = 'new' | 'available' | 'existing';
const AssignSubscription: React.FC<AssignSubscriptionProps> = ({
  contracts,
  license,
  canPurchaseAndAssign,
  onGrantLicense,
  onOrderAndAssignNewLicense,
}) => {
  const [contractOption, setContractOption] = React.useState<ContractOption>();
  const [contractId, setContractId] = React.useState<string | null>(null);
  const [licenseId, setLicenseId] = React.useState<string>();

  const handleAssignClick = React.useCallback(() => {
    if (!contractId) {
      return;
    }
    if (contractOption === 'new') {
      onOrderAndAssignNewLicense(contractId);
    }
    if (contractOption === 'available' && !!licenseId) {
      onGrantLicense(contractId, licenseId);
    }
    if (contractOption === 'existing' && licenseId) {
      onGrantLicense(contractId, licenseId);
    }
  }, [contractId, contractOption, licenseId, onGrantLicense, onOrderAndAssignNewLicense]);
  const handleSelectContract = React.useCallback(
    (option: ContractOption, cId: string | null) => () => {
      setContractOption(option);
      setContractId(cId);
    },
    []
  );
  const handleSelectAvailableContract = React.useCallback(
    (option: ContractOption, cId: string, lId: string) => () => {
      setContractOption(option);
      setContractId(cId);
      setLicenseId(lId);
    },
    []
  );
  const handleSelectContractLicense = React.useCallback(
    (cId: string, lId: string) => () => {
      setContractId(cId);
      setLicenseId(lId);
    },
    []
  );
  const availableContracts = React.useMemo(() => {
    return contracts
      .map((contract) => {
        const licenses = contract.licenses.filter((license) => license.starts_at === null);
        return {
          ...contract,
          licenses,
        };
      })
      .filter((c) => c.licenses.length);
  }, [contracts]);
  const unavailableContracts = React.useMemo(() => {
    return contracts
      .filter((contract) => {
        const licenses = contract.licenses.filter((license) => license.starts_at === null);
        return licenses.length === 0;
      })
      .reduce<ContractData[]>((grouped, contract: ContractData) => {
        const findIndex = grouped.findIndex((c) => contract.license_type === c.license_type);
        if (findIndex < 0) {
          grouped.push(contract);
          return grouped;
        }
        const oldContract = grouped[findIndex];
        if (new Date(oldContract.created_at) < new Date(contract.created_at)) {
          grouped[findIndex] = contract;
        }
        return grouped;
      }, []);
  }, [contracts]);
  const startedAndAvailableContractLicenses = React.useMemo<LicenseData[]>(
    () =>
      contracts.flatMap((contract) =>
        contract.licenses
          .filter((license) => !license.assigned_to && license.assignments_left > 0 && !!license.starts_at)
          .map((license: LicenseData) => {
            return { ...license, contract };
          })
      ),
    [contracts]
  );
  React.useEffect(() => {
    if (
      !contractOption &&
      !availableContracts.length &&
      (!canPurchaseAndAssign || !unavailableContracts.length) &&
      startedAndAvailableContractLicenses.length
    ) {
      setContractOption('existing');
    }
  }, [
    contractOption,
    availableContracts,
    canPurchaseAndAssign,
    unavailableContracts,
    startedAndAvailableContractLicenses,
  ]);
  const nothingLeft = React.useMemo(() => {
    return (
      !availableContracts.length &&
      (!canPurchaseAndAssign || !unavailableContracts.length) &&
      !startedAndAvailableContractLicenses.length
    );
  }, [availableContracts, canPurchaseAndAssign, unavailableContracts, startedAndAvailableContractLicenses]);
  return (
    <>
      <div className="pb-4">
        <h4 className="mt-4 mb-4"> {t('licenseManagement.title')}</h4>
        {nothingLeft && <p>{t('licenseManagement.noSubscriptionsLeft')}</p>}
        {availableContracts.map((availabelContract) => (
          <RadioButton
            id={`grant-${availabelContract.id}`}
            key={`grant-${availabelContract.id}`}
            label={t('licenseManagement.options.useNew', {
              licenseType: availabelContract.license_type,
              count: availabelContract.licenses.length,
            })}
            value={`${availabelContract.id};available`}
            checked={contractOption === 'available' && availabelContract.id === contractId}
            onCheckedChange={handleSelectAvailableContract(
              'available',
              availabelContract.id,
              availabelContract.licenses[0].id
            )}
          />
        ))}
        {canPurchaseAndAssign &&
          unavailableContracts.map((unavailabelContract) => (
            <RadioButton
              id={`grant-${unavailabelContract.id}`}
              key={`grant-${unavailabelContract.id}`}
              label={t('licenseManagement.options.purchaseAssign', {
                licenseType: unavailabelContract.license_type,
              })}
              value={`${unavailabelContract.id};new`}
              checked={contractOption === 'new' && contractId === unavailabelContract.id}
              onCheckedChange={handleSelectContract('new', unavailabelContract.id)}
            />
          ))}
        {!!startedAndAvailableContractLicenses.length && (
          <RadioButton
            id={`grant-existing`}
            key={`grant-existing`}
            label={`${t('licenseManagement.useExistingSubscription')} (${startedAndAvailableContractLicenses.length})`}
            value={`existing`}
            checked={contractOption === 'existing'}
            onCheckedChange={handleSelectContract('existing', null)}
            disabled={!!license}
          />
        )}
        {!!startedAndAvailableContractLicenses.length && contractOption === 'existing' && (
          <>
            <h4 className="mt-4 mb-4">{t('licenseManagement.selectExisting')}</h4>
            {startedAndAvailableContractLicenses.map((availableLicense) => (
              <RadioButton
                id={`grant-${availableLicense.id}`}
                key={`grant-${availableLicense.id}`}
                label={t(
                  availableLicense.expires_at
                    ? 'licenseManagement.selectExistingOption'
                    : 'licenseManagement.selectExistingOptionNoExpiry',
                  {
                    type: `${availableLicense.contract.license_type}${
                      availableLicense.contract.reference && ` (${availableLicense.contract.reference})`
                    }`,
                    assignmentsLeft: availableLicense.assignments_left,
                    expireDate: formatDate(availableLicense.expires_at),
                  }
                )}
                checked={availableLicense.id === licenseId}
                onCheckedChange={handleSelectContractLicense(availableLicense.contract.id, availableLicense.id)}
              />
            ))}
          </>
        )}
      </div>
      <div className="AssignSubscription--Footer">
        <Button
          color="blue"
          label={t('licenseManagement.assign')}
          onClick={handleAssignClick}
          disabled={!contractId}
          className="mb-4"
        />
      </div>
    </>
  );
};
export default AssignSubscription;
